import { client } from "@/api/client.ts";
import { APP_ID } from "@/env.ts";
import { SOURCE_VIDAPP } from "@/api/const.ts";
import { ItemType, ThumbnailSize, Video } from "@/api/content.ts";
import { AxiosResponse } from "axios";

const BASE_URL = "https://user.vidapp.com";
const TUNNEL_URL = `${BASE_URL}/tunnel/${APP_ID}`;
const OPEN_TUNNEL_URL = `${BASE_URL}/tunnel/${APP_ID}/open`;

export interface CreateCustomer {
  Name: string;
  Email: string;
  Secret: string;
  DataSource?: string; // Should typically be VidApp
}

export interface Comment {
  SourceCommentId: number;
  Comment: string;
  CommentDate: string;
  Name: string; // Author
  Thumbnail: string; // Author Profile;
  Responses: Comment[];
  SourceCustomerId?: string;
  EditedAt?: string;
}

export interface PutComment {
  comment: string;
  userId: string;
  userName: string;
  itemId: number;
  itemType: string;
  dataSource: string;
  sourceId: string;
  parentCommentId?: number;
  thumbnailSource?: string;
}

export interface DeleteComment {
  sourceCommentId: number;
  sourceItemId: string;
  userId: string;
}

export interface PutEvent extends Record<string, unknown> {
  DataSource: string;
  ItemId: number;
  ItemType: string;
  SourceId?: string;
  EventType: string;
  EventDate: string;
  ItemParentId?: number;
  ItemCourseId?: number;
  EventData?: string;
}

export type DeleteEvent = Omit<PutEvent, "SourceId">;

export interface ProgressEvent extends Record<string, unknown> {
  ItemId: number;
  ItemType: string;
  SourceId: string;
  ProgressSeconds: string;
  PercentComplete: string;
  DataSource: string;
  Date: string;
  CustomerEmail: string;
  Type?: string;
}

export interface RequestDeletion {
  user_id: string;
}

export interface FilterOption {
  Id: number;
  Option: string;
  Position: number;
  SourceTag: string;
  IsDefault: boolean;
  Enabled: boolean;
}

export interface Filter {
  Id: number;
  Filter: string;
  Position: number;
  Enabled: boolean;
  Source: string;
  FilterOptions: FilterOption[];
}

export interface Favourite {
  Id: number;
  SourceCustomerId: string;
  SourceItemId: string;
  ItemId: string;
  ItemType: ItemType;
  DataSource: string;
}

export interface UpdateDefaultPlaylist {
  ItemId: string;
  ItemType: ItemType;
  SourceItemId: string;
  UserId: string;
}

export interface FavouritesResponse {
  Favourites: Favourite[];
}

export interface WatchlistItem {
  Id: number;
  SourceItemId: string;
  ItemId: string;
  ItemType: ItemType;
  DataSource: string;
}

export interface WatchlistResponse {
  Watchlist: WatchlistItem[];
}

export interface UserEvent {
  DataSource: string;
  ItemId: string;
  ItemType: ItemType;
  SourceId: string;
  EventType: string;
  EventDate: string;
  EventData: string;
  ItemParentId?: string;
  ItemCourseId?: string;
}

export interface UserEventResponse {
  Events: UserEvent[];
}

export interface Playlist {
  PlaylistId: number;
  PlaylistName: string;
  Position: number;
  CreatedDate: string;
  Items: PlaylistItem[];
}

export interface PlaylistItem {
  ItemId: number;
  ItemType: ItemType;
  Position: number;
}

export interface PlaylistResponse {
  Playlists: Playlist[];
}

export interface UpdatePlaylistItem {
  ItemId: number;
  ItemType: ItemType;
  AddToPlaylists: number[];
  RemoveFromPlaylists: number[];
}

export interface CreatePlaylist {
  PlaylistName: string;
  ThumbnailSource?: string;
}

export interface NotificationPreferenceResponse {
  communities: string[];
  commentsAndReplies: boolean;
  newPosts: boolean;
  postByAdmins: boolean;
  reactions: boolean;
}

type NotificationData = {
  sender_id: string;
  content_id: string;
  avatar_url?: string;
  display_name?: string;
  is_user_notification?: boolean;
};

export interface Notification {
  id: string;
  type: string;
  heading: string;
  content: string;
  data?: NotificationData;
  send_after: number;
  community_id: string;
  sort_id: string;
  url: string;
  canceled?: boolean;
}

// Community group in VidApp DB - Mix of Amity fields and VidApp custom fields
export interface CommunityGroup {
  CommunityID: string;
  Type: "main" | "optin" | "automatic";
  Position: number;
  Name: string;
  Description: string;
  BannerURL: string | null;
  ThumbnailURL: string | null;
  CreatedAt: string;
}

interface TunnelRequest {
  path: string;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  payload?: Record<string, unknown>;
  open?: boolean;
}

async function tunnelRequest<T>(
  request: TunnelRequest,
): Promise<AxiosResponse<T | { error: string }>> {
  const url = request.open ? OPEN_TUNNEL_URL : TUNNEL_URL;

  return await client.post<T | { error: string }>(url, {
    HttpMethod: request.method ?? "GET",
    HttpPayload: request.payload ?? {},
    HttpPath: request.path,
  });
}

export const createCustomer = async (props: CreateCustomer) => {
  const { data } = await tunnelRequest<{ error: string }>({
    open: true,
    path: `/app/${APP_ID}/customers`,
    method: "POST",
    payload: {
      DataSource: "VidApp",
      Platform: "Web",
      ...props,
    },
  });

  if ("error" in data) {
    console.error(`Failed to create customer ${props.Email}`, data);
    return;
  }
  return data;
};

export const getComments = async ({
  itemId,
  dataSource,
  sourceId,
}: {
  itemId: number;
  dataSource: string;
  sourceId: string;
}) => {
  const { data } = await tunnelRequest<{
    Comments?: Comment[];
  }>({
    path: `/app/${APP_ID}/comments?DataSource=${dataSource}&ItemId=${itemId}&ItemType=video&SourceItemId=${sourceId}`,
  });

  if ("error" in data) {
    console.error(`Failed to retrieve comments for video ${itemId}`);
    return [];
  }
  return data?.Comments ?? [];
};

export const putComment = async ({
  comment,
  userName,
  userId,
  itemId,
  itemType,
  dataSource,
  sourceId,
  parentCommentId,
  thumbnailSource,
}: PutComment) => {
  const payload: Record<string, unknown> = {
    DataSource: dataSource,
    ItemId: itemId,
    ItemType: itemType,
    SourceItemId: sourceId,
    CustomerId: userId,
    Comment: comment,
    CustomerName: userName,
    ThumbnailSource: thumbnailSource,
  };
  if (parentCommentId) {
    payload["SourceParentCommentId"] = parentCommentId; // Replies
  }

  const { data } = await tunnelRequest<{
    Comments?: Comment[];
  }>({
    path: `/app/${APP_ID}/comments`,
    method: "PUT",
    payload: payload,
  });

  if ("error" in data) {
    console.error(`Failed to add comment for video ${itemId}`);
    return [];
  }
  return data?.Comments ?? [];
};

export interface PatchComment {
  Comment: string;
  SourceCommentId: number;
  SourceItemId: string;
  CustomerId: string;
}

export const updateComment = async (payload: PatchComment) => {
  const { data } = await tunnelRequest<Comment>({
    path: `/app/${APP_ID}/comments`,
    method: "PATCH",
    payload: payload as unknown as Record<string, unknown>,
  });

  if ("error" in data) {
    console.error(
      `Failed to update comment with SourceCommentId: ${payload.SourceCommentId}`,
    );
    return undefined;
  }
  return data;
};

export const deleteComment = async ({
  sourceCommentId,
  sourceItemId,
  userId,
}: DeleteComment) => {
  const { data } = await tunnelRequest<{
    success?: boolean;
  }>({
    path: `/app/${APP_ID}/comments`,
    method: "DELETE",
    payload: {
      SourceCommentId: sourceCommentId,
      SourceItemId: sourceItemId,
      CustomerId: userId,
    },
  });

  if ("error" in data) {
    console.error(`Failed to delete comment ${sourceCommentId}`);
    return false;
  }
  return data.success ?? false;
};

export const archiveComment = async ({
  sourceCommentId,
  sourceItemId,
  userId,
}: DeleteComment) => {
  const { data } = await tunnelRequest<{
    success?: boolean;
  }>({
    path: `/app/${APP_ID}/archive-comment`,
    method: "POST",
    payload: {
      SourceCommentId: sourceCommentId,
      SourceItemId: sourceItemId,
      CustomerId: userId,
    },
  });

  if ("error" in data) {
    console.error(`Failed to archive comment ${sourceCommentId}`);
    return false;
  }
  return data.success ?? false;
};
export const putEvent = async ({
  UserId,
  ...payloadIn
}: PutEvent & {
  UserId: string;
}) => {
  const payload: PutEvent = { ...payloadIn };
  const { data } = await tunnelRequest<{ Events: unknown[] }>({
    path: `/app/${APP_ID}/customers/${UserId}/event/v2`,
    method: "PUT",
    payload: payload,
  });

  if ("error" in data) {
    console.error(`Failed to add event`, { ...payload, UserId });
  }
  return data;
};

export const deleteEvent = async ({
  UserId,
  ...payloadIn
}: DeleteEvent & {
  UserId: string;
}) => {
  const payload: DeleteEvent = { ...payloadIn };
  const { data } = await tunnelRequest<{ Events: unknown[] }>({
    path: `/app/${APP_ID}/customers/${UserId}/event/v2`,
    method: "DELETE",
    payload: payload,
  });

  if ("error" in data) {
    console.error(`Failed to delete event`, { ...payload, UserId });
  }
  return data;
};

export const putProgressEvent = async ({
  UserId,
  ...payloadIn
}: ProgressEvent & { UserId: string }) => {
  const payload: ProgressEvent = { ...payloadIn };
  const { data } = await tunnelRequest<{ status: string }>({
    path: `/app/${APP_ID}/tracking/${UserId}`,
    method: "POST",
    payload: payload,
  });

  if ("error" in data) {
    console.error(
      `Failed to add progress event for ${payload.ItemId} on User ${UserId}`,
    );
  }
};

export const getFilters = async () => {
  const { data } = await tunnelRequest<Filter[]>({
    path: `/app/${APP_ID}/filters`,
  });
  if ("error" in data) {
    console.error("Failed to lookup filters");
    return [];
  }
  return data;
};

export const getFavourites = async ({ user_id }: { user_id: string }) => {
  const { data } = await tunnelRequest<FavouritesResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/favourites?DataSource=VidApp`,
  });
  if ("error" in data) {
    console.error("Failed to lookup favourites", data.error);
    return { Favourites: [] } as FavouritesResponse;
  }
  return data;
};

export const putFavourite = async ({
  UserId,
  ItemId,
  ItemType,
  SourceItemId,
}: UpdateDefaultPlaylist) => {
  const { data } = await tunnelRequest<FavouritesResponse>({
    path: `/app/${APP_ID}/customers/${UserId}/favourites`,
    method: "PUT",
    payload: {
      ItemId,
      ItemType,
      SourceItemId,
      DataSource: SOURCE_VIDAPP,
    },
  });
  if ("error" in data) {
    console.error("Failed to put favourite", data.error);
    return undefined;
  }
  return data;
};

export const deleteFavourite = async ({
  UserId,
  ItemId,
  ItemType,
  SourceItemId,
}: UpdateDefaultPlaylist) => {
  const { data } = await tunnelRequest<FavouritesResponse>({
    path: `/app/${APP_ID}/customers/${UserId}/favourites`,
    method: "DELETE",
    payload: {
      ItemType,
      ItemId,
      SourceItemId,
      DataSource: SOURCE_VIDAPP,
    },
  });
  if ("error" in data) {
    console.error("Failed to delete favourite", data.error);
    return undefined;
  }
  return data;
};

export const getWatchlist = async ({ user_id }: { user_id: string }) => {
  const { data } = await tunnelRequest<WatchlistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/watchlist?DataSource=VidApp`,
  });
  if ("error" in data) {
    console.error("Failed to lookup watchlist", data.error);
    return { Watchlist: [] } as WatchlistResponse;
  }
  return data;
};

export const putWatchlistItem = async ({
  UserId,
  ItemId,
  ItemType,
  SourceItemId,
}: UpdateDefaultPlaylist) => {
  const { data } = await tunnelRequest<WatchlistResponse>({
    path: `/app/${APP_ID}/customers/${UserId}/watchlist`,
    method: "PUT",
    payload: {
      ItemId,
      ItemType,
      SourceItemId,
      DataSource: SOURCE_VIDAPP,
    },
  });
  if ("error" in data) {
    console.error("Failed to put watchlist item", data.error);
    return undefined;
  }
  return data;
};
export const deleteWatchlistItem = async ({
  UserId,
  ItemType,
  ItemId,
  SourceItemId,
}: UpdateDefaultPlaylist) => {
  const { data } = await tunnelRequest<WatchlistResponse>({
    path: `/app/${APP_ID}/customers/${UserId}/watchlist`,
    method: "DELETE",
    payload: {
      ItemType,
      ItemId,
      SourceItemId,
      DataSource: SOURCE_VIDAPP,
    },
  });
  if ("error" in data) {
    console.error("Failed to delete watchlist item", data.error);
    return undefined;
  }
  return data;
};

export const getCustomPlaylists = async ({ user_id }: { user_id: string }) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists`,
  });
  if ("error" in data) {
    console.error("Failed to get playlists", data.error);
    return undefined;
  }
  return data;
};

export const createPlaylist = async ({
  user_id,
  ...payload
}: CreatePlaylist & { user_id: string }) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists`,
    method: "POST",
    payload: payload,
  });
  if ("error" in data) {
    console.error("Failed to create playlist", data.error);
    return undefined;
  }
  return data;
};

export const putItemInPlaylists = async ({
  user_id,
  ...payload
}: UpdatePlaylistItem & { user_id: string }) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists/item`,
    method: "POST",
    payload,
  });
  if ("error" in data) {
    console.error("Failed to update item in playlists", data.error);
    return undefined;
  }
  return data;
};

export const deletePlaylist = async ({
  user_id,
  PlaylistId,
}: { user_id: string } & Pick<Playlist, "PlaylistId">) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists/${PlaylistId}`,
    method: "DELETE",
  });
  if ("error" in data) {
    console.error("Failed to delete playlist", data.error);
    return undefined;
  }
  return data;
};

export const renamePlaylist = async ({
  user_id,
  PlaylistId,
  PlaylistName,
}: { user_id: string } & Pick<Playlist, "PlaylistId" | "PlaylistName">) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists/${PlaylistId}`,
    method: "PUT",
    payload: { PlaylistName },
  });
  if ("error" in data) {
    console.error("Failed to rename playlist", data.error);
    return undefined;
  }
  return data;
};
export const reorderPlaylists = async ({
  user_id,
  playlists,
}: {
  user_id: string;
  playlists: Playlist[];
}) => {
  const { data } = await tunnelRequest<PlaylistResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/playlists`,
    method: "PUT",
    payload: { Playlists: playlists },
  });
  if ("error" in data) {
    console.error("Failed to reorder playlists", data.error);
    return undefined;
  }
  return data;
};

export const requestDeletion = ({ user_id }: RequestDeletion) => {
  return client.post(TUNNEL_URL, {
    HttpPath: `/app/${APP_ID}/users/${user_id}/request-deletion`,
    HttpMethod: "POST",
    HttpPayload: { data_source: SOURCE_VIDAPP },
  });
};

export const getUserEvents = async ({ user_id }: { user_id: string }) => {
  const { data } = await tunnelRequest<UserEventResponse>({
    path: `/app/${APP_ID}/customers/${user_id}/event/v2`,
  });
  if ("error" in data) {
    console.error("Failed to get UserEvents", data.error);
    return undefined;
  }
  return data;
};

export interface PatchUser {
  Email?: string;
  Secret?: string; // Required for email change
  FirstName: string;
  LastName?: string;
  AvatarURL?: string;
  AvatarIcon?: string;
  MarketingOptIn?: boolean;
}

const BAD_PASSWORD_ERROR = "Invalid password. Failed to update your email.";
const UNEXPECTED_ERROR = "Failed to update user details";

export const updateUser = async ({
  UserId,
  Email,
  FirstName,
  LastName,
  Secret,
  AvatarIcon,
  AvatarURL,
  MarketingOptIn,
}: PatchUser & { UserId: string }) => {
  return await client
    .post(TUNNEL_URL, {
      HttpPath: `/app/${APP_ID}/customers`,
      HttpMethod: "PATCH",
      HttpPayload: {
        DataSource: SOURCE_VIDAPP,
        SourceCustomerId: UserId,
        FirstName,
        LastName,
        Email,
        Secret,
        AvatarIcon,
        AvatarURL,
        MarketingOptIn,
      },
    })
    .then((response) => {
      if (response.data?.error?.includes("password is required")) {
        throw new Error(BAD_PASSWORD_ERROR);
      }
      if (response.status > 399 || !!response.data?.error) {
        throw new Error(UNEXPECTED_ERROR);
      }
      return response.data;
    });
};

export interface CourseCompletion {
  percentage_completion: number;
  watched_videos: string[];
  course_video_count: number;
  next_video: Omit<Video, "Description" | "Files">;
  next_video_parent_id: string;
}
export interface CourseCompletionExtended extends CourseCompletion {
  course_name: string;
  course_id: number;
  course_thumbnails: Partial<Record<ThumbnailSize, string>>;
  latest_event_date: string;
}
export interface CourseCompletionAll {
  courses: CourseCompletionExtended[];
}

export const getCourseCompletion = async ({
  userID,
  courseID,
}: {
  userID: string;
  courseID: number;
}) => {
  const { data } = await tunnelRequest<CourseCompletion>({
    path: `/app/${APP_ID}/users/${userID}/course_completion?course_id=${courseID}`,
  });
  if ("error" in data) {
    console.error("Failed to get course completion", data?.error);
    return undefined;
  }
  return data;
};

export const getCourseCompletionAll = async ({
  userID,
}: {
  userID: string;
}) => {
  const { data } = await tunnelRequest<CourseCompletionAll>({
    path: `/app/${APP_ID}/users/${userID}/course_completion/all`,
  });
  if ("error" in data) {
    console.error("Failed to get course completion", data?.error);
    return undefined;
  }
  return data;
};

export const getNotificationPreferences = async ({
  user_id,
}: {
  user_id: string;
}) => {
  const { data } = await tunnelRequest<NotificationPreferenceResponse>({
    path: `/app/${APP_ID}/users/${user_id}/notification-preference`,
  });
  if ("error" in data) {
    console.error("Failed to get Notification Preferences", data.error);
    return undefined;
  }
  return data;
};

export const updateNotificationPreferences = async ({
  user_id,
  payload,
}: {
  user_id: string;
  payload: Partial<NotificationPreferenceResponse>; // Only need to provide the parameters being updated
}) => {
  const { data } = await tunnelRequest<NotificationPreferenceResponse>({
    path: `/app/${APP_ID}/users/${user_id}/notification-preference`,
    method: "PUT",
    payload,
  });
  if ("error" in data) {
    console.error("Failed to reorder playlists", data.error);
    return undefined;
  }
  return data;
};

export const getNotifications = async ({ uid }: { uid: string }) => {
  const { data } = await tunnelRequest<{ notifications: Notification[] }>({
    path: `/app/${APP_ID}/notification?SourceCustomerId=${uid}`,
  });
  if ("error" in data) {
    console.error("Failed to get notifications", data.error);
    return undefined;
  }
  return data.notifications;
};

export const getCommunityGroups = async () => {
  const { data } = await tunnelRequest<{ Groups: CommunityGroup[] }>({
    path: `/app/${APP_ID}/v1/community/groups`,
  });
  if ("error" in data) {
    console.error("Failed to get community groups", data.error);
    return undefined;
  }
  return data.Groups;
};
